import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Alert } from "flowbite-react";
import { BiError } from "react-icons/bi";
import { 
  FileSpreadsheet, 
  Trophy, 
  Users, 
  Settings, 
  CheckCircle,
  ArrowRight 
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { updateUserOnboarding } from "services/auth.service";
import { AuthContext } from "context/AuthContext";

import "./styles.scss";

export function OnboardingGestor() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleNext = async () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      // Última etapa - finalizar onboarding
      try {
        setLoading(true);
        setErrorMessage("");

        if (!user?._id) {
          throw new Error("Usuário não autenticado");
        }

        await updateUserOnboarding("", user._id);
        
        setTimeout(() => {
          window.location.href = "/gestao";
        }, 1500);
      } catch (error: any) {
        setLoading(false);
        setErrorMessage(error.message || "Erro ao finalizar onboarding");
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <FileSpreadsheet size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Importar Dados</h1>
            <p className="onboarding-description">
              Aqui você conecta seu Google Sheets para trazer as informações que alimentarão seus rankings.
            </p>
            <p className="onboarding-description">
              Acompanhe também o histórico das importações e possíveis erros.
            </p>
          </div>
        );
      case 1:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <Trophy size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Rankings</h1>
            <p className="onboarding-description">
              Crie e gerencie seus rankings.
            </p>
            <p className="onboarding-description">
              Defina métrica, período, layout visual e escolha os participantes de cada ranking.
            </p>
          </div>
        );
      case 2:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <Users size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Participantes</h1>
            <p className="onboarding-description">
              Gerencie todos os usuários da sua empresa.
            </p>
            <p className="onboarding-description">
              Envie convites via email e importe novos participantes.
            </p>
          </div>
        );
      case 3:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <Settings size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Configurações</h1>
            <p className="onboarding-description">
              Ajuste informações da sua conta e preferências gerais da empresa.
            </p>
          </div>
        );
      case 4:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon success">
              <CheckCircle size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Pronto!</h1>
            <p className="onboarding-description">
              Agora você conhece as principais áreas do Rankifier Gestão.
            </p>
            <p className="onboarding-description">
              Comece importando seus dados ou criando seu primeiro ranking.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Bem-vindo ao Rankifier Gestão</title>
      </Helmet>
      <div
        id="onboarding-wrapper"
        className="w-full min-h-screen flex items-center justify-center"
      >
        <div className="onboarding-card">
          {renderStep()}

          {!!errorMessage && (
            <Alert color="failure" icon={BiError} className="mt-4">
              {errorMessage}
            </Alert>
          )}

          <div className="onboarding-actions">
            <Button
              onClick={handleNext}
              disabled={loading}
              className="onboarding-button-primary"
            >
              <span>
                {loading 
                  ? "Aguarde..." 
                  : currentStep === 4 
                  ? "Ir para o Painel" 
                  : "Próximo"}
              </span>
              {!loading && <ArrowRight size={20} />}
            </Button>
          </div>

          <div className="onboarding-dots">
            {[0, 1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={`dot ${currentStep === step ? "active" : ""} ${
                  currentStep > step ? "completed" : ""
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
